import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { StackItem } from 'ui/layout/StackItem'
import Section from 'ui/layout/Section'

import { makeStyles } from 'tss-react/mui'
import type { Theme } from '@mui/material/styles'

import type { DepartmentListItemProps } from 'types/common'
import type { PreReservation } from 'types/med'
import {
  formatYearDateJpDayOfWeekTime,
  parseDatetimeSecondString,
} from 'ui/utils/dateFormatUtils'
import { Text1 } from 'ui/shorthands/Texts'

const useStyles = makeStyles()((theme: Theme) => ({
  undefined: {
    color: theme.palette.text.disabled,
  },
}))

const reformat = (dateTimeSecondString: string) =>
  formatYearDateJpDayOfWeekTime(parseDatetimeSecondString(dateTimeSecondString))

const Undefined: React.FC = () => {
  const { t } = useTranslation()
  const { classes } = useStyles()
  return <div className={classes.undefined}>{t('未指定')}</div>
}

type PreReservationReadProps = {
  preReservation: PreReservation
  departments: DepartmentListItemProps[]
}

const PreReservationRead: React.FC<PreReservationReadProps> = ({
  preReservation,
  departments,
}: PreReservationReadProps) => {
  const { t } = useTranslation()
  const department = useMemo(
    () =>
      departments.find(
        (d) => d.id === preReservation.provisional_reservation_department_id
      ),
    [departments, preReservation.provisional_reservation_department_id]
  )

  const expiredDatetime = formatYearDateJpDayOfWeekTime(
    parseDatetimeSecondString(preReservation.access_key_expiration)
  )

  return (
    <>
      <Section>
        <StackItem label={t('受付科目')}>
          <Text1>{department?.name ?? <Undefined />}</Text1>
        </StackItem>
        <StackItem label={t('日時')}>
          <Text1>
            {preReservation?.provisional_reservation_datetime_start ? (
              `${reformat(
                preReservation.provisional_reservation_datetime_start
              )}〜`
            ) : (
              <Undefined />
            )}
          </Text1>
        </StackItem>
        <StackItem label={t('氏名')}>
          <Text1>{preReservation?.applicant_name || <Undefined />}</Text1>
        </StackItem>
        {preReservation?.applicant_phone && (
          <StackItem label={t('電話番号')}>
            <Text1>{preReservation?.applicant_phone}</Text1>
          </StackItem>
        )}
        {preReservation?.applicant_email && (
          <StackItem label={t('メールアドレス')}>
            <Text1>{preReservation?.applicant_email}</Text1>
          </StackItem>
        )}
      </Section>
      <Section>
        <StackItem label={t('前回送信日時')}>
          <Text1>{reformat(preReservation.sms_last_sent_at)}</Text1>
        </StackItem>
        <StackItem label={t('有効期限')}>
          <Text1>{expiredDatetime}</Text1>
        </StackItem>
      </Section>
    </>
  )
}

export default PreReservationRead
